import type { MergeExclusive } from 'type-fest';
import type { VanillaLinkProps } from '../Link/index.ts';
import clsx from 'clsx';
import { Spinner } from '../Spinner/index.ts';

const styles = {
  color: {
    default: {
      default: clsx(['bg-white text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50']),
      primary: clsx(['bg-primary-600 text-white hover:bg-primary-500']),
      success: clsx(['bg-emerald-600 text-white hover:bg-emerald-500']),
      danger: clsx(['bg-red-600 text-white hover:bg-red-500']),
      warning: clsx(['bg-amber-600 text-white hover:bg-amber-500'])
    },

    light: {
      default: clsx(['bg-gray-100 text-gray-900 hover:bg-gray-200']),
      primary: clsx(['bg-primary-50 text-primary-800 hover:bg-primary-100']),
      success: clsx(['bg-emerald-50 text-emerald-800 hover:bg-emerald-100']),
      danger: clsx(['bg-red-50 text-red-800 hover:bg-red-100']),
      warning: clsx(['bg-amber-50 text-amber-800 hover:bg-amber-100'])
    },

    outline: {
      default: clsx(['text-gray-900 ring-1 ring-gray-900/15 ring-inset hover:bg-gray-900/2.5']),
      primary: clsx([
        'text-primary-600 ring-1 ring-primary-600/30 ring-inset hover:bg-primary-600/2.5'
      ]),
      success: clsx([
        'text-emerald-600 ring-1 ring-emerald-600/30 ring-inset hover:bg-emerald-600/2.5'
      ]),
      danger: clsx(['text-red-600 ring-1 ring-red-600/30 ring-inset hover:bg-red-600/2.5']),
      warning: clsx(['text-amber-600 ring-1 ring-amber-600/30 ring-inset hover:bg-amber-600/2.5'])
    },

    subtle: {
      default: clsx(['text-gray-900 hover:bg-gray-900/5']),
      primary: clsx(['text-primary-600 hover:bg-primary-600/5']),
      success: clsx(['text-emerald-600 hover:bg-emerald-600/5']),
      danger: clsx(['text-red-600 hover:bg-red-600/5']),
      warning: clsx(['text-amber-600 hover:bg-amber-600/5'])
    },

    plain: {
      default: clsx('text-gray-800 hover:text-gray-700'),
      primary: clsx('text-primary-600 hover:text-primary-500'),
      success: clsx('text-emerald-600 hover:text-emerald-500'),
      danger: clsx('text-red-600 hover:text-red-500'),
      warning: clsx('text-amber-600 hover:text-amber-500')
    }
  },

  padding: {
    base: {
      xs: clsx('px-2 py-1'),
      sm: clsx('px-2.5 py-1.5'),
      md: clsx('px-3 py-2'),
      lg: clsx('px-3.5 py-2.5'),
      xl: clsx('px-4 py-3')
    },
    icon: {
      xs: clsx('min-h-6 min-w-6 p-1'),
      sm: clsx('min-h-8 min-w-8 p-1.5'),
      md: clsx('min-h-9 min-w-9 p-2'),
      lg: clsx('min-h-10 min-w-10 p-2.5'),
      xl: clsx('min-h-12 min-w-12 p-3')
    }
  },

  text: {
    xs: clsx('text-xs data-large-text-mobile:text-sm sm:data-large-text-mobile:text-xs'),
    sm: clsx('text-sm data-large-text-mobile:text-base sm:data-large-text-mobile:text-sm'),
    md: clsx('text-sm data-large-text-mobile:text-base sm:data-large-text-mobile:text-sm'),
    lg: clsx('text-sm data-large-text-mobile:text-base sm:data-large-text-mobile:text-sm'),
    xl: clsx('text-base')
  },

  icon: {
    xs: clsx('flex place-content-center place-items-center *:size-3'),
    sm: clsx('flex place-content-center place-items-center *:size-3.75'),
    md: clsx('flex place-content-center place-items-center *:size-4'),
    lg: clsx('flex place-content-center place-items-center *:size-5'),
    xl: clsx('flex place-content-center place-items-center *:size-5')
  }
};

type BaseButtonProps = React.PropsWithChildren<{
  color?: 'default' | 'primary' | 'success' | 'danger' | 'warning';
  disabled?: boolean;
  fillContainer?: boolean | 'mobile';
  icon?: React.ReactNode;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  largeTextMobile?: boolean;
  loading?: boolean;
  onClick?: React.MouseEventHandler;
  rounded?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  tabIndex?: number;
  variant?: 'default' | 'light' | 'outline' | 'plain' | 'subtle';
}>;

export type ButtonProps = BaseButtonProps &
  MergeExclusive<
    {
      renderLink: (props: VanillaLinkProps) => React.ReactNode;
    } & Pick<React.ComponentPropsWithRef<'a'>, 'ref'>,
    {
      renderLink?: never;
    } & Pick<React.ComponentPropsWithRef<'button'>, 'ref' | 'type'>
  >;

export function Button(props: ButtonProps) {
  const { loading = false, ref, renderLink } = props;

  const buttonProps = useButtonProps(props);

  const disabled = props.disabled || loading;

  if (renderLink && !disabled) {
    return renderLink({
      children: <ButtonContent {...props} />,
      plain: true,
      ref,
      ...buttonProps
    });
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      data-disabled={disabled ? '' : undefined}
      disabled={disabled}
      type={props.type ?? 'button'}
      {...buttonProps}
    >
      <ButtonContent {...props} />
    </button>
  );
}

function ButtonContent({
  children,
  icon,
  iconLeft,
  iconRight,
  loading = false,
  size = 'md'
}: BaseButtonProps) {
  return (
    <>
      {!!iconLeft && size !== 'xs' && (
        <div className={clsx(styles.icon[size], loading && 'opacity-0', '-ml-0.5')}>{iconLeft}</div>
      )}

      <div className={clsx('min-w-0', icon && styles.icon[size], loading && 'opacity-0')}>
        {icon || children}
      </div>

      {!!iconRight && size !== 'xs' && (
        <div className={clsx(styles.icon[size], loading && 'opacity-0', '-mr-0.5')}>
          {iconRight}
        </div>
      )}

      {loading && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
          <Spinner className="size-4" />
        </div>
      )}
    </>
  );
}

function useButtonProps({
  color = 'default',
  disabled = false,
  fillContainer = false,
  icon,
  largeTextMobile = false,
  loading = false,
  onClick,
  rounded = false,
  size = 'md',
  tabIndex,
  variant = 'default'
}: BaseButtonProps) {
  const isDisabled = disabled || loading;

  return {
    'data-large-text-mobile': largeTextMobile ? 'true' : undefined,

    className: clsx(
      'relative items-center justify-center gap-x-1.5 font-semibold whitespace-nowrap',

      // Disabled
      'data-disabled:pointer-events-none data-disabled:cursor-not-allowed',
      variant === 'plain' || variant === 'subtle'
        ? 'data-disabled:text-gray-300'
        : 'data-disabled:bg-gray-100 data-disabled:text-gray-400 data-disabled:ring-gray-200',

      // Focus outline
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',

      // Shadow
      variant === 'default' && !icon && !isDisabled && 'shadow-xs',

      // Width
      fillContainer === true && 'flex w-full',
      fillContainer === 'mobile' && 'w-full sm:w-auto',
      !fillContainer && 'inline-flex',

      // Rounded corners
      rounded ? 'rounded-full' : size == 'xs' ? 'rounded' : 'rounded-md',

      // Color
      styles.color[variant][color],

      // Padding
      variant !== 'plain' && (icon ? styles.padding.icon[size] : styles.padding.base[size]),

      // Text size
      styles.text[size]
    ),
    onClick: !isDisabled ? onClick : undefined,
    tabIndex
  };
}
